import React from 'react'
import { WarningOutlined } from '@ant-design/icons'
import isNil from 'lodash/isNil'
import {
  GrantRow,
  GrantRowDetail,
  GrantRowEmail,
  GrantRowList,
  GrantSection,
  GrantSectionHeader,
  GrantSectionHint,
  GrantSectionTitle,
  WarningNote,
} from '../UserManagement/UserManagement.styles'
import {
  RemovalChip,
  RemovalChips,
  RemovalCompanyRow,
  RemovalLoss,
  RemovalNotice,
  RemovalScroll,
} from './ClientBillingProfileManagement.styles'
import type {
  RecipientRemovalKind,
  RecipientRemovalLine,
} from './recipientRemoval'

interface RecipientRemovalNoticeProps {
  lines: RecipientRemovalLine[]
  /** False adds a note that their portal access stays as it is. */
  canRevoke: boolean
  /** Said once below everyone, such as a company left with nobody. */
  warning?: string | null
}

const byKind = (lines: RecipientRemovalLine[], kind: RecipientRemovalKind) =>
  lines.filter((line) => line.kind === kind)

/** Emails as chips when they share one company, else one row each. */
const renderInvoiceOnly = (lines: RecipientRemovalLine[]) => {
  const companies = new Set(lines.map((line) => line.companyNames))
  if (companies.size === 1) {
    return (
      <RemovalChips>
        {lines.map((line) => (
          <RemovalChip key={line.email}>{line.email}</RemovalChip>
        ))}
      </RemovalChips>
    )
  }
  return (
    <GrantRowList>
      {lines.map((line) => (
        <GrantRow key={line.email}>
          <RemovalCompanyRow>
            <GrantRowEmail>{line.email}</GrantRowEmail>
            <GrantRowDetail>{line.companyNames}</GrantRowDetail>
          </RemovalCompanyRow>
        </GrantRow>
      ))}
    </GrantRowList>
  )
}

/**
 * Who a removal affects, most affected first: people who lose access get a
 * line each, people who only stop getting invoices are listed together.
 */
const RecipientRemovalNotice = ({
  lines,
  canRevoke,
  warning = null,
}: RecipientRemovalNoticeProps) => {
  if (lines.length === 0) return null

  const losing = byKind(lines, 'access')
  const invoiceOnly = byKind(lines, 'invoices')
  const stillInvoiced = byKind(lines, 'stillInvoiced')
  const sharedCompany =
    new Set(invoiceOnly.map((line) => line.companyNames)).size === 1
      ? invoiceOnly[0]?.companyNames
      : null

  return (
    <RemovalNotice>
      <RemovalScroll>
        {losing.length > 0 ? (
          <GrantSection>
            <GrantSectionHeader>
              <GrantSectionTitle>
                Losing access ({losing.length})
              </GrantSectionTitle>
            </GrantSectionHeader>
            <GrantRowList>
              {losing.map((line) => (
                <GrantRow key={line.email}>
                  <GrantRowEmail>{line.email}</GrantRowEmail>
                  <RemovalLoss>{line.text}</RemovalLoss>
                </GrantRow>
              ))}
            </GrantRowList>
          </GrantSection>
        ) : null}

        {invoiceOnly.length > 0 ? (
          <GrantSection>
            <GrantSectionHeader>
              <GrantSectionTitle>
                {isNil(sharedCompany)
                  ? `Will stop getting invoices (${invoiceOnly.length})`
                  : `Will stop getting ${sharedCompany} invoices (${invoiceOnly.length})`}
              </GrantSectionTitle>
            </GrantSectionHeader>
            {renderInvoiceOnly(invoiceOnly)}
          </GrantSection>
        ) : null}

        {stillInvoiced.length > 0 ? (
          <GrantSection>
            <GrantSectionHeader>
              <GrantSectionTitle>
                No change ({stillInvoiced.length})
              </GrantSectionTitle>
              <GrantSectionHint>
                Another billing profile for this company still lists them.
              </GrantSectionHint>
            </GrantSectionHeader>
            <RemovalChips>
              {stillInvoiced.map((line) => (
                <RemovalChip key={line.email}>{line.email}</RemovalChip>
              ))}
            </RemovalChips>
          </GrantSection>
        ) : null}
      </RemovalScroll>

      {isNil(warning) ? null : (
        <WarningNote>
          <WarningOutlined />
          <span>{warning}</span>
        </WarningNote>
      )}
      {canRevoke ? null : (
        <GrantSectionHint>
          You can&apos;t change their portal access, so it stays as it is.
        </GrantSectionHint>
      )}
    </RemovalNotice>
  )
}

export { RecipientRemovalNotice }
